// src/pages/Admin/StatistiquesComptabilite/DashboardCompta.jsx

import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";

import {
  FaCalculator,
} from "react-icons/fa";

import { useComptaDashboard } from "./hooks/useComptaDashboard";
import { COMPTA_SECTIONS } from "./data/comptaSections";

import ComptaAccessDenied from "./components/ComptaAccessDenied";
import ComptaHero from "./components/ComptaHero";
import ComptaSeasonPanel from "./components/ComptaSeasonPanel";
import ComptaQuickNav from "./components/ComptaQuickNav";
import ComptaMobileSection from "./components/ComptaMobileSection";

/**
 * Composants fonctionnels repris de la version Dashboard desktop.
 *
 * Ajuste seulement ces chemins si ton dossier DashboardCompta n'est pas
 * exactement ici.
 */
import ComptaKpiCards from "../../../dashboard/pages/admin/DashboardCompta/components/ComptaKpiCards";
import PaymentStatusChart from "../../../dashboard/pages/admin/DashboardCompta/components/PaymentStatusChart";
import PaymentTypeChart from "../../../dashboard/pages/admin/DashboardCompta/components/PaymentTypeChart";
import MonthlyPurchasesChart from "../../../dashboard/pages/admin/DashboardCompta/components/MonthlyPurchasesChart";
import BoutiqueSalesPanel from "../../../dashboard/pages/admin/DashboardCompta/components/BoutiqueSalesPanel";

function renderSectionContent({
  sectionKey,
  stats,
  loadingReglements,
  selectedSeason,
  seasons,
  setSelectedSeason,
}) {
  switch (sectionKey) {
    case "statuts":
      return (
        <PaymentStatusChart
          data={stats.statutReglements}
          loading={loadingReglements}
        />
      );

    case "types":
      return (
        <PaymentTypeChart
          data={stats.typeReglements}
          loading={loadingReglements}
        />
      );

    case "achatsMensuels":
      return (
        <MonthlyPurchasesChart
          selectedSeason={selectedSeason}
          seasons={seasons}
          onSeasonChange={setSelectedSeason}
        />
      );

    case "boutique":
      return <BoutiqueSalesPanel selectedSeason={selectedSeason} />;

    default:
      return null;
  }
}

export default function DashboardCompta() {
  useScrollToTop();

  const {
    userRole,
    isAdmin,

    seasons,
    selectedSeason,
    setSelectedSeason,

    stats,
    loadingSettings,
    loadingReglements,
    loading,
    errorMsg,

    openSections,
    openedCount,
    toggleSection,
    openAll,
    closeAll,

    refresh,
  } = useComptaDashboard();

  if (!isAdmin) {
    return (
      <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
        <ScrollIndicator />

        <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

        <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
          <ComptaAccessDenied />
        </main>

        <ToastContainer />
      </section>
    );
  }

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-white">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Administrateur"
          title="Statistiques de comptabilité"
          subtitle="Analyse les règlements, cotisations, réductions, Pass’Sport et achats boutique du club."
          icon={FaCalculator}
          align="left"
          className="mb-4"
        />

        <div className="mb-5 sm:mb-7">
          <DashboardNavigation role={userRole} />
        </div>

        <ComptaHero
          selectedSeason={selectedSeason}
          stats={stats}
          loading={loading}
          onRefresh={refresh}
        />

        <div className="mt-4 space-y-4 sm:mt-6">
          <ComptaSeasonPanel
            seasons={seasons}
            selectedSeason={selectedSeason}
            onSeasonChange={setSelectedSeason}
            loadingSettings={loadingSettings}
            loadingReglements={loadingReglements}
            errorMsg={errorMsg}
            onRefresh={refresh}
            openedCount={openedCount}
            onOpenAll={openAll}
            onCloseAll={closeAll}
          />

          <ComptaKpiCards stats={stats} loading={loadingReglements} />

          <section className="overflow-hidden rounded-[2rem] border border-white/70 bg-white/85 shadow-xl shadow-slate-200/70 backdrop-blur dark:border-slate-800/80 dark:bg-slate-900/70 dark:shadow-black/20">
            <div className="border-b border-slate-200/70 bg-white/70 px-3 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-5">
              <ComptaQuickNav
                sections={COMPTA_SECTIONS}
                openSections={openSections}
              />
            </div>

            <div className="space-y-4 p-3 sm:p-5 lg:p-6">
              {COMPTA_SECTIONS.map((section) => (
                <ComptaMobileSection
                  key={section.key}
                  id={section.id}
                  eyebrow={section.eyebrow}
                  title={section.title}
                  description={section.description}
                  icon={section.icon}
                  isOpen={!!openSections[section.key]}
                  onToggle={() => toggleSection(section.key)}
                >
                  {renderSectionContent({
                    sectionKey: section.key,
                    stats,
                    loadingReglements,
                    selectedSeason,
                    seasons,
                    setSelectedSeason,
                  })}
                </ComptaMobileSection>
              ))}
            </div>
          </section>
        </div>
      </main>

      <ToastContainer />
    </section>
  );
}